import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist';
import { CircularProgress, Box } from '@mui/material';
import { useEstimator } from '../../context/EstimatorContext';
const Plot = createPlotlyComponent(Plotly);

const ProjectBudgetPieChart = forwardRef(({ onImageGenerated }, ref) => {
  const { budgetSummaryRows } = useEstimator();
  const [chartData, setChartData] = useState(null);
  const [layout, setLayout] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isExportMode, setIsExportMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const exportDivId = 'plotly-export-container';

  useImperativeHandle(ref, () => ({
    async getImageDataUrl() {
      if (!chartData || !layout) return null;

      try {
        // Render hidden chart to offscreen div
        const gd = await Plotly.newPlot(exportDivId, chartData, layout, {
          displayModeBar: false,
        });

        const imgData = await Plotly.toImage(gd, {
          format: 'png',
          height: 600,
          width: 600,
        });

        setImageSrc(imgData);
        setIsExportMode(true);
        if (onImageGenerated) onImageGenerated(imgData);
        return imgData;
      } catch (err) {
        console.error('Plotly export error:', err);
        return null;
      }
    },
    clearExportMode() {
      setIsExportMode(false);
    },
  }));

  useEffect(() => {
    if (!budgetSummaryRows || budgetSummaryRows.length === 0) return;

    const getRaw = (no) => budgetSummaryRows.find((r) => r.no === no)?.rawPrice || 0;

    const row = getRaw(5);
    const materials = getRaw(12);
    const construction = getRaw(17);
    const pm = getRaw(25);

    const values = [row, materials, construction, pm];
    const labels = ['ROW', 'Materials', 'Construction', 'Project Management'];

    if (values.every((v) => v === 0)) {
      setLoading(false);
      return;
    }

    const pieTrace = {
      labels,
      values,
      type: 'pie',
      marker: {
        colors: ['#0e2756', '#bfbfbf', '#3b66a5', '#b0c3e5'],
      },
      hoverinfo: 'label+percent+value',
      textinfo: 'label+percent',
    };

    setChartData([pieTrace]);

    setLayout({
      margin: { l: 30, r: 30, t: 50, b: 30 },
      showlegend: true,
      autosize: true,
      legend: { orientation: 'h', x: 0.5, xanchor: 'center', y: -0.2 },
    });

    setLoading(false);
  }, [budgetSummaryRows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 400 }}>
      {/* Visible chart */}
      {!isExportMode ? (
        <Plot
          data={chartData}
          layout={layout}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: '100%', height: '100%' }}
        />
      ) : (
        <img src={imageSrc} alt="Chart Snapshot" style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }} />
      )}

      {/* Hidden offscreen export div */}
      <div id={exportDivId} style={{ display: 'none', width: 600, height: 400 }}></div>
    </Box>
  );
});

export default ProjectBudgetPieChart;
